'use client'

import { type Variants, motion } from 'framer-motion'
import Link from 'next/link'
import { useRouter } from 'next/navigation'

import {
  Build,
  ErrorOutline,
  Home,
  Refresh,
  Schedule,
  Settings,
  SupportAgent,
  Warning,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { useState } from 'react'

type MaintenanceVariant = 'maintenance' | 'error' | 'unavailable' | 'upgrade'

interface UnderMaintenanceProps {
  /** Variant determines the icon, colors, and default messaging */
  variant?: MaintenanceVariant
  /** Custom title override */
  title?: string
  /** Custom description override */
  description?: string
  /** Expected return time or duration */
  expectedDuration?: string
  /** Show retry button */
  showRetry?: boolean
  /** Retry callback function */
  onRetry?: () => void
  /** Show home/dashboard button */
  showHomeButton?: boolean
  /** Custom home URL */
  homeUrl?: string
  /** Show contact support option */
  showSupport?: boolean
  /** Support contact info */
  supportEmail?: string
  /** Error details (shown in dev mode only) */
  errorDetails?: string
  /** Error digest/ID */
  errorId?: string | undefined
  /** Whether this is rendered within dashboard layout */
  isDashboard?: boolean
  /** Additional action buttons */
  actions?: React.ReactNode
}

// Animation variants
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
}

const iconVariants: Variants = {
  hidden: { scale: 0.8, opacity: 0, rotate: -10 },
  visible: {
    scale: 1,
    opacity: 1,
    rotate: 0,
    transition: {
      type: 'spring',
      stiffness: 150,
      damping: 12,
    },
  },
}

// Variant configurations
const variantConfig: Record<
  MaintenanceVariant,
  {
    icon: React.ReactNode
    color: 'warning' | 'error' | 'info' | 'primary'
    title: string
    description: string
    chipLabel: string
  }
> = {
  maintenance: {
    icon: <Build sx={{ fontSize: { xs: 44, sm: 52 } }} />,
    color: 'warning',
    title: 'Under Maintenance',
    description:
      'We are currently performing scheduled maintenance to improve our services. Please check back shortly.',
    chipLabel: 'Scheduled Maintenance',
  },
  error: {
    icon: <ErrorOutline sx={{ fontSize: { xs: 44, sm: 52 } }} />,
    color: 'error',
    title: 'Something Went Wrong',
    description:
      'We encountered an unexpected error. Our team has been notified and is working to resolve the issue.',
    chipLabel: 'System Error',
  },
  unavailable: {
    icon: <Warning sx={{ fontSize: { xs: 44, sm: 52 } }} />,
    color: 'warning',
    title: 'Service Unavailable',
    description:
      'This service is temporarily unavailable. We apologize for the inconvenience and are working to restore access.',
    chipLabel: 'Temporarily Unavailable',
  },
  upgrade: {
    icon: <Settings sx={{ fontSize: { xs: 44, sm: 52 } }} />,
    color: 'info',
    title: 'System Upgrade in Progress',
    description:
      'We are upgrading our system to bring you new features and improvements. Thank you for your patience.',
    chipLabel: 'System Upgrade',
  },
}

/**
 * Under Maintenance Component
 * Comprehensive component for displaying maintenance, error, and unavailable states.
 * Consistent with the ETH Pass Portal design system.
 */
export default function UnderMaintenance({
  variant = 'maintenance',
  title,
  description,
  expectedDuration,
  showRetry = true,
  onRetry,
  showHomeButton = true,
  homeUrl = '/dashboard',
  showSupport = true,
  supportEmail = 'support@ethiopassport.gov.et',
  errorDetails,
  errorId,
  isDashboard = true,
  actions,
}: UnderMaintenanceProps) {
  const theme = useTheme()
  const router = useRouter()
  const [showDetails, setShowDetails] = useState(false)
  const config = variantConfig[variant]

  const colorMap = {
    warning: theme.palette.warning,
    error: theme.palette.error,
    info: theme.palette.info,
    primary: theme.palette.primary,
  }
  const accentColor = colorMap[config.color]

  const handleRetry = () => {
    if (onRetry) {
      onRetry()
    } else {
      router.refresh()
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: isDashboard ? 'calc(100vh - 100px)' : '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 3, sm: 4, md: 6 },
        position: 'relative',
        bgcolor: isDashboard ? 'transparent' : theme.palette.background.default,
      }}
    >
      {/* Background decorations */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: { xs: 100, md: 160 },
          height: { xs: 100, md: 160 },
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(accentColor.main, 0.08)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          left: '8%',
          width: { xs: 70, md: 120 },
          height: { xs: 70, md: 120 },
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          width: '100%',
          maxWidth: 520,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Stack spacing={3.5} alignItems="center" textAlign="center">
          {/* Icon Section */}
          <motion.div variants={iconVariants}>
            <Box
              sx={{
                width: { xs: 90, sm: 110 },
                height: { xs: 90, sm: 110 },
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${accentColor.main} 0%, ${accentColor.dark} 100%)`,
                boxShadow: `0 16px 48px ${alpha(accentColor.main, 0.35)}`,
                color: 'white',
              }}
            >
              {config.icon}
            </Box>
          </motion.div>

          {/* Status Chip */}
          <motion.div variants={itemVariants}>
            <Chip
              label={config.chipLabel}
              size="medium"
              sx={{
                height: 32,
                px: 1.5,
                bgcolor: alpha(accentColor.main, 0.1),
                color: accentColor.main,
                fontWeight: 600,
                fontSize: '0.8rem',
                borderRadius: 2,
                border: `1px solid ${alpha(accentColor.main, 0.25)}`,
              }}
            />
          </motion.div>

          {/* Title & Description */}
          <motion.div variants={itemVariants}>
            <Stack spacing={1.5}>
              <Typography
                variant="h4"
                fontWeight={700}
                color="text.primary"
                sx={{
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  letterSpacing: '-0.02em',
                }}
              >
                {title || config.title}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  maxWidth: 420,
                  mx: 'auto',
                  lineHeight: 1.7,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                {description || config.description}
              </Typography>
            </Stack>
          </motion.div>

          {/* Expected Duration */}
          {expectedDuration && (
            <motion.div variants={itemVariants}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  px: 2.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                }}
              >
                <Schedule sx={{ fontSize: 20, color: 'info.main' }} />
                <Typography variant="body2" color="info.main" fontWeight={600}>
                  Expected Duration: {expectedDuration}
                </Typography>
              </Stack>
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div variants={itemVariants}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{ width: '100%', mt: 1 }}
            >
              {showRetry && (
                <Button
                  variant="contained"
                  startIcon={<Refresh />}
                  onClick={handleRetry}
                  sx={{
                    minWidth: 140,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  }}
                >
                  Try Again
                </Button>
              )}
              {showHomeButton && (
                <Button
                  component={Link}
                  href={homeUrl}
                  variant="outlined"
                  startIcon={<Home />}
                  sx={{
                    minWidth: 140,
                    borderWidth: 1.5,
                    '&:hover': { borderWidth: 1.5 },
                  }}
                >
                  Go Home
                </Button>
              )}
              {actions}
            </Stack>
          </motion.div>

          {/* Error Details (Dev Mode Only) */}
          {process.env.NODE_ENV === 'development' && (errorDetails || errorId) && (
            <motion.div variants={itemVariants} style={{ width: '100%' }}>
              <Box sx={{ width: '100%' }}>
                <Button
                  size="small"
                  onClick={() => setShowDetails(!showDetails)}
                  sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                >
                  {showDetails ? 'Hide' : 'Show'} Error Details
                </Button>
                <Collapse in={showDetails}>
                  <Box
                    sx={{
                      mt: 1,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.error.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.15)}`,
                      textAlign: 'left',
                    }}
                  >
                    {errorId && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mb: 1, color: 'error.main', fontWeight: 600 }}
                      >
                        Error ID: {errorId}
                      </Typography>
                    )}
                    {errorDetails && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          color: 'text.secondary',
                          wordBreak: 'break-all',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {errorDetails}
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>
            </motion.div>
          )}

          <Divider sx={{ width: '80%', my: 1 }} />

          {/* Support Section */}
          {showSupport && (
            <motion.div variants={itemVariants}>
              <Stack spacing={1} alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <SupportAgent sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Need assistance?
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  component="a"
                  href={`mailto:${supportEmail}`}
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {supportEmail}
                </Typography>
              </Stack>
            </motion.div>
          )}

          {/* Footer Branding */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ mt: 2, display: 'block' }}
            >
              Ethiopian Passport Services • Secure Identity Management
            </Typography>
          </motion.div>
        </Stack>
      </motion.div>
    </Box>
  )
}
